import { filter, take, takeUntil, map, catchError } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import {
  ApplicationRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  AfterViewInit,
  PLATFORM_ID,
  APP_ID,
  ViewChild,
  signal,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { GoogleDataLayerService } from './services/google-data-layer.service';
import { isPlatformBrowser, Location } from '@angular/common';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { UserModel } from './models';
import {
  NavigationStart,
  Router,
  RouterOutlet,
  NavigationEnd,
  ActivatedRoute,
} from '@angular/router';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { LazyLoadScriptService } from './services/lazy-load-script.service';
import { MENUS } from './models/data/menus';
import { UserIdleService } from 'angular-user-idle';
import { BannerComponent } from './shared-components/banner/banner.component';
import { RibbonComponent } from './shared-components/ribbon/ribbon.component';
import { SystemBannerComponent } from './shared-components/system-banner/system-banner.component';
import { AppModal } from './uikit/modal/modal.component';
import { AppButton } from './uikit/button/button.component';
import { AppToast } from './uikit/toast/toast.component';
import { AppToastService } from './uikit/toast/toast.service';
import { AmaFooterComponent } from './shared-components/ama-footer/ama-footer.component';
import { NavigationComponent } from './shared-components/navigation/navigation.component';
import { UserService, PromotionService } from './services';
import { provideNgxMask } from 'ngx-mask';
import { CustomTextService } from './services/custom-text.service';
import { DashboardQueueService } from './services/dashboard-queue.service';
import { GatingService } from './services/gating.service';
import { InstitutionService } from './services/institution.service';
import { NavigationService } from './services/navigation.service';
import { SavedSearchService } from './services/saved-search.service';
import { SearchService } from './services/search.service';
import { SpecialtyService } from './services/specialty.service';
import { VacantPositionService } from './services/vacant-position.service';
import { AciListService } from './services/aci-list.service';
import { AciService } from './services/aci.service';
import { Actions, ActionType, ofActionDispatched, Store } from '@ngxs/store';
import { UserActions } from './store/user/actions/user.actions';
import { UserStateModel } from './store/user/state/user.state';
import { DashboardService } from './services/dashboard.service';
import { DashboardApiService } from './store/services/dashboard-api.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { ApiEnvironmentService } from './app-initializer/api-environment/api-environment.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { nrAction } from './helpers/newrelic.helper';
import { SessionService } from './services/session.service';

export let browserRefresh = false;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    RouterOutlet,
    BannerComponent,
    RibbonComponent,
    SystemBannerComponent,
    AppModal,
    AppButton,
    AppToast,
    AmaFooterComponent,
    NavigationComponent,
    AngularSvgIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    CookieService,
    UserService,
    LazyLoadScriptService,
    // UserIdleService is configured at root level via provideUserIdleConfig() in app.config.ts
    // Do NOT provide it here or it will create a new instance without the config
    AuthService,
    GoogleDataLayerService,
    PromotionService,
    CustomTextService,
    DashboardQueueService,
    DashboardService,
    GatingService,
    InstitutionService,
    NavigationService,
    SavedSearchService,
    SearchService,
    SpecialtyService,
    VacantPositionService,
    AciListService,
    AciService,
    DashboardApiService,
    provideNgxMask(),
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly sessionService = inject(SessionService);

  private subscriptions: Subscription[] = [];
  private getCurrentUserSubscription: Subscription;
  public getMenuItemsSubscription: Subscription;
  private readonly siteMenus: string[] = ['product', 'main', 'global', 'footer'];
  public bannerPromotions = toSignal(
    this.promotionService.getAnnouncementBanner().pipe(
      map((promos) => {
        const list = promos || [];
        return list.length ? [list[0]] : [];
      }),
      catchError(() => of([])),
    ),
    { initialValue: [] },
  );
  public systemWideMessage = toSignal(
    this.promotionService.getSystemWideMessage().pipe(
      map((promos) => {
        const list = promos || [];
        return list.length ? promos : [];
      }),
      catchError(() => of([])),
    ),
    { initialValue: [] },
  );

  public amaExploreMenuItems = MENUS.globalExplore;
  public freidaNavigation = MENUS.freidaNavigation;
  public title = 'Freida';
  public offline: boolean;
  public promptEvent: any;
  public deferredPrompt: any;
  public showIdleModal = signal(false);
  public showUserLoggedOutModal = signal(false);
  public userMenuGroup: any;


  public get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
  public bannerIsActivated: boolean = false;
  public jwtCookieValue: any;
  public closePromoBanner: boolean = false;
  public currentUser = this.userService.defaultUser;
  public browserRefreshSubscription: Subscription;
  public version = environment.version;
  private idleSubscriptionsSetup = false;
  private suppressNextIdleModal = false;

  @ViewChild('ribbon') ribbon!: RibbonComponent;

  public getUserMenuGroup() {
    if (isPlatformBrowser(this.platformId)) {
      return [
        {
          text: this.authService.getAuthToken().length === 0 ? 'Sign In' : 'Sign Out',
          link:
            this.authService.getAuthToken().length === 0
              ? this.authService.getSamlSignInUrl(window.location.href)
              : this.authService.getSamlSignOutUrl(window.location.href, window.location.hostname),
        },
        {
          text: 'Become a Member',
          link: 'https://member.ama-assn.org/join-renew/',
        },
        {
          text: 'Create Free Account',
          link: 'https://fsso.ama-assn.org/sign-up',
        },
      ];
    }

    return {};
  }

  public user = signal([this.userService.defaultUser]);

  public user$: Observable<UserStateModel>;
  public currentUser$: Observable<UserModel>;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private googleDataLayerService: GoogleDataLayerService,
    private userService: UserService,
    private authService: AuthService,
    private promotionService: PromotionService,
    private router: Router,
    private lazyLoadService: LazyLoadScriptService,
    private userIdle: UserIdleService,
    private cookieService: CookieService,
    private route: ActivatedRoute,
    private appRef: ApplicationRef,
    private apiEnvironmentService: ApiEnvironmentService,
    private store: Store,
    private actions$: Actions,
    private toastService: AppToastService,
    private location: Location,
    @Inject(APP_ID) private appId: string,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.user$ = this.store.select<UserStateModel>((state: any) => state.user);
    this.userMenuGroup = this.getUserMenuGroup();

    const navEndEvents = this.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    navEndEvents.pipe(takeUntil(this.destroy$)).subscribe((event: NavigationEnd) => {
      this.googleDataLayerService.trackAnalytics({
        event: 'page',
        pageName: event.urlAfterRedirects,
      });

      this.updatePreviousUrl(event.urlAfterRedirects);
    });

    // Retrying a failed dashboard write fails again, so ask the user to sign in again instead
    const signInAgainMessage = 'Please sign out and sign back in, then try again.';
    const dashboardFailureToasts: Array<[ActionType, string]> = [
      [UserActions.AddProgramFail, "We couldn't save this program"],
      [UserActions.RemoveProgramFail, "We couldn't remove this program"],
      [UserActions.RemoveAllProgramsFail, "We couldn't remove these programs"],
      [UserActions.UpdateAciSurveyDashboardFail, "We couldn't update your ACI results"],
    ];

    dashboardFailureToasts.forEach(([failAction, summary]) => {
      this.actions$
        .pipe(ofActionDispatched(failAction), takeUntil(this.destroy$))
        .subscribe(() => {
          this.toastService.showError(summary, signInAgainMessage);
        });
    });
  }

  /** Load basic user only (name, life stage, member status) for fast first paint. */
  @Dispatch() public getUser = () => new UserActions.UpdateUser();
  @Dispatch() public updatePreviousUrl = (url: string) => new UserActions.UpdatePreviousUrl(url);

  public stayLoggedIn() {
    this.showIdleModal.set(false);

    this.suppressNextIdleModal = true;
    setTimeout(() => (this.suppressNextIdleModal = false), 500);

    nrAction('stay_logged_in');
    this.userIdle.stopTimer();
    this.userIdle.resetTimer();
    this.getUser();
    this.sessionService.refreshSession();
  }

  public ngOnInit() {
    if (isPlatformBrowser(this.platformId) && window) {
      if (sessionStorage.getItem('bannerAcknowlegement') === 'true') {
        this.bannerIsActivated = true;
      }

      // Check if user was logged out due to expired/invalid session.
      // Do NOT remove the flag here — keep it in sessionStorage so the modal
      // survives tab discard/reload. It is cleared when the user interacts
      // with the modal (signInButton or onUserLoggedOutModalClosed).
      if (sessionStorage.getItem('showUserLoggedOutModal') === 'true') {
        this.showUserLoggedOutModal.set(true);
        // Clear auto-login flag so it can't get stuck while the modal is showing.
        sessionStorage.removeItem('autoLoginInProgress');
      }

      if (environment.bypassAuth) {
        if (!this.showUserLoggedOutModal() && this.authService.getAuthToken()) {
          this.getUser();
        }
        // Skip all SSO/cookie checks in bypass mode
      } else {
        // If signInButton() triggered a logout→login cycle, we arrive here at the FREIDA homepage
        // after the SAML logout completes (the logout destination chain to login doesn't survive the
        // IdP round-trip). Detect this state and call login() directly to continue the sign-in.
        // At this point the Drupal session was just invalidated by the logout, so we won't be
        // auto-redirected to /user/{uid}.
        if (sessionStorage.getItem('redirectToLoginAfterLogout') === 'true') {
          sessionStorage.removeItem('redirectToLoginAfterLogout');
          if (!this.cookieService.check('FreidaJwt')) {
            this.authService.login();
            return;
          }
        }

        // If the user just dismissed the logged-out modal, skip all cookie checks for this load
        // so we don't immediately re-trigger auto-login against their will.
        if (sessionStorage.getItem('userDismissedLoggedOutModal') === 'true') {
          sessionStorage.removeItem('userDismissedLoggedOutModal');
          sessionStorage.removeItem('staleJwtLoginAttempted');
          sessionStorage.removeItem('autoLoginInProgress');
          return;
        }

        // The JWT expires after 1 hour but its cookie lasts 3. Drop an expired one first so the checks
        // below see "no FreidaJwt" and take the existing silent re-login / signed-out paths.
        this.authService.discardExpiredJwt();

        const pdHSessionId = this.cookieService.get('PD-H-SESSION-ID');

        // Completely unauthenticated — no FreidaJwt, no PD-H session, never signed in.
        // Route guards handle any redirect; skip API calls to avoid the interceptor
        // triggering SSO via window.location.href before the guard navigation completes.
        if (
          !this.cookieService.check('FreidaJwt') &&
          !pdHSessionId &&
          (!isPlatformBrowser(this.platformId) || localStorage.getItem('isSignedIn') !== 'true')
        ) {
          return;
        }

        // If a stale JWT login was already attempted, check if it succeeded.
        // If session is still invalid, clear all cookies and show the session expired modal.
        // Using window.location.href (full reload) instead of router.navigateByUrl so that
        // ngOnInit re-runs and picks up the showUserLoggedOutModal sessionStorage flag.
        // A client-side router nav would not re-run checkIsSignedIn(), and the
        // redirectIfAuthenticated guard would immediately bounce the user back to
        // /dashboard-landing if FreidaJwt is still present.
        if (sessionStorage.getItem('staleJwtLoginAttempted') === 'true') {
          sessionStorage.removeItem('staleJwtLoginAttempted');
          if (
            !this.showUserLoggedOutModal() &&
            !this.cookieService.check('FreidaJwt') &&
            !pdHSessionId.match('^1')
          ) {
            // SSO recovery was attempted but session is still invalid. Clear remaining
            // stale cookies and show the session expired modal so the user can sign in
            // explicitly. Avoid any navigation here — the modal handles the next step.
            this.authService.clearSessionCookies();
            this.showUserLoggedOutModal.set(true);
            sessionStorage.setItem('showUserLoggedOutModal', 'true');
            return;
          }
          // Login attempt completed (FreidaJwt exists or PD-H is now valid). Load the user
          // and stop — do NOT fall through to the stale-JWT re-check below, which would
          // delete the fresh FreidaJwt and loop back to login().
          if (!this.showUserLoggedOutModal()) {
            this.getUser();
          }
          return;
        }

        // FreidaJwt must always co-exist with a valid PD-H session (starting with '1').
        // If FreidaJwt exists but PD-H is missing or invalid, delete the stale JWT and attempt login.
        if (
          !this.showUserLoggedOutModal() &&
          this.cookieService.check('FreidaJwt') &&
          !pdHSessionId.match('^1')
        ) {
          this.cookieService.delete('FreidaJwt', '/', '.ama-assn.org');
          this.cookieService.delete('FreidaJwt');
          sessionStorage.setItem('staleJwtLoginAttempted', 'true');
          this.authService.login();
          return;
        }

        if (!this.showUserLoggedOutModal() && pdHSessionId.match('^1')) {
          if (!this.cookieService.check('FreidaJwt')) {
            // PD-H is valid but FreidaJwt is missing — go through login to create it.
            const autoLoginInProgress = sessionStorage.getItem('autoLoginInProgress') === 'true';
            if (!autoLoginInProgress) {
              sessionStorage.setItem('autoLoginInProgress', 'true');
              this.authService.login();
              return;
            }
            // Auto-login was attempted but FreidaJwt still not created.
            // PD-H is still valid so the SSO session is active — do not show the modal
            // or clear cookies. Clear the flag and proceed as guest so the next
            // navigation can retry.
            sessionStorage.removeItem('autoLoginInProgress');
            return;
          }
          // Session fully established — clear any stale auto-login flag.
          sessionStorage.removeItem('autoLoginInProgress');
          // PD-H is valid and FreidaJwt exists — proceed to getUser().
        } else if (!this.showUserLoggedOutModal() && pdHSessionId && !pdHSessionId.match('^1')) {
          // PD-H exists but is invalid and no FreidaJwt — attempt login to create a fresh session.
          const autoLoginInProgress = sessionStorage.getItem('autoLoginInProgress') === 'true';
          if (!autoLoginInProgress) {
            sessionStorage.setItem('autoLoginInProgress', 'true');
            this.authService.reAuthenticate();
            return;
          }
          // Re-auth was attempted but failed — show session expired modal.
          sessionStorage.removeItem('autoLoginInProgress');
          this.authService.clearSessionCookies();
          this.showUserLoggedOutModal.set(true);
          sessionStorage.setItem('showUserLoggedOutModal', 'true');
          return;
        }

        if (
          !this.showUserLoggedOutModal() &&
          (this.cookieService.check('FreidaJwt') || pdHSessionId)
        ) {
          // Only call getUser() when there is an actual session. If isSignedIn is set in
          // localStorage but no JWT/PD-H cookies exist, the user clicked Sign In but
          // navigated back before completing SSO — skip the API call to avoid a 401
          // triggering the interceptor's login() redirect and the session-expired modal.
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('isSignedIn', 'true');
          }
          this.getUser();
        }
      } // end else (bypassAuth)
    }

    this.setUpHashRedirects();

    // Only subscribe to user state in browser (not during SSR)
    // Store subscriptions are hot observables that prevent Angular from stabilizing
    if (isPlatformBrowser(this.platformId)) {
      // get User Selector from Store, set user var if user is available.
      this.getCurrentUserSubscription = this.user$
        .pipe(takeUntil(this.destroy$))
        .subscribe((user) => {
          this.currentUser = user.user;

          if (this.currentUser) {
            this.user.set([this.currentUser]);

            if (this.currentUser.tam_id) {
              this.googleDataLayerService.setUserAnalyticsData(this.currentUser);
            }

            if (
              this.currentUser.uuid &&
              !this.idleSubscriptionsSetup &&
              !this.showUserLoggedOutModal()
            ) {
              this.idleSubscriptionsSetup = true;
              this.userIdle.startWatching();

              this.userIdle
                .onTimerStart()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  if (this.suppressNextIdleModal) {
                    return;
                  }
                  this.showIdleModal.set(true);
                  nrAction('user_idle_warning_shown');
                });

              this.userIdle
                .onTimeout()
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                  if (this.showIdleModal()) {
                    this.userIdle.stopTimer();
                    this.userIdle.stopWatching();
                    this.showIdleModal.set(false);
                    sessionStorage.setItem('showUserLoggedOutModal', 'true');
                    nrAction('session_timed_out');
                    this.authService.logout(undefined, true);
                  }
                });
            }
          } else {
            this.user.set([this.userService.defaultUser]);
          }
        });
    } // End isPlatformBrowser check for user$ subscription

    if (isPlatformBrowser(this.platformId) && document) {
      // Load analytics scripts after app stabilizes to reduce TBT / JS execution time during initial load
      if (isPlatformBrowser(this.platformId) && window) {
        this.appRef.isStable
          .pipe(
            filter((stable: boolean) => stable === true),
            take(1),
            takeUntil(this.destroy$),
          )
          .subscribe(() => {
            const newRelicEnv = this.apiEnvironmentService.newRelicScript;
            if (newRelicEnv === 'prod') {
              this.lazyLoadService
                .loadScript('/assets/scripts/newrelic-prod.js')
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe(() => {
                  (window as any).newrelic?.setCustomAttribute('environment', 'prod');
                });
            } else if (newRelicEnv === 'test') {
              this.lazyLoadService
                .loadScript('/assets/scripts/newrelic-staging.js')
                .pipe(take(1), takeUntil(this.destroy$))
                .subscribe(() => {
                  (window as any).newrelic?.setCustomAttribute('environment', 'test');
                });
            }
          });
      }
    }
  }

  public ngAfterViewInit() {
    this.ribbon?.burgerButton?.focusBegin?.nativeElement.focus();
  }

  public learnMoreClicked(bannerUrl) {
    if (isPlatformBrowser(this.platformId) && window) {
      window.open(bannerUrl[0].ctaURL, '_blank');
    }
  }

  public closeSystemBanner() {
    if (isPlatformBrowser(this.platformId) && window) {
      this.bannerIsActivated = true;
      sessionStorage.setItem('bannerAcknowlegement', 'true');
    }
  }

  private setUpHashRedirects() {
    const redirectPattern = /^\/(Freida)?#/i;

    const routerNavigationSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe((event: NavigationStart) => {
        if (!!event.url && event.url.match(redirectPattern)) {
          this.router.navigateByUrl(event.url.replace(redirectPattern, ''));
        }
      });

    this.subscriptions.push(routerNavigationSubscription);
  }

  /** Called when user closes the session-expired modal (X or overlay). Hide modal and persist so it is not shown again right afterwards. */
  public onUserLoggedOutModalClosed() {
    // p-dialog fires onHide (and thus afterClose) whenever visible becomes false — including
    // when signInButton() programmatically sets showUserLoggedOutModal = false. Guard against
    // treating that as a user dismissal; the sign-in flow handles its own redirect.
    if (sessionStorage.getItem('redirectToLoginAfterLogout') === 'true') {
      return;
    }
    this.showUserLoggedOutModal.set(false);
    nrAction('session_expired_modal_dismissed');
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem('showUserLoggedOutModal');
      sessionStorage.setItem('userDismissedLoggedOutModal', 'true');
      window.location.href = '/';
    }
  }

  public signInButton() {
    sessionStorage.removeItem('showUserLoggedOutModal');
    sessionStorage.removeItem('userDismissedLoggedOutModal');
    sessionStorage.removeItem('autoLoginInProgress');
    sessionStorage.removeItem('staleJwtLoginAttempted');
    this.showUserLoggedOutModal.set(false);
    nrAction('sign_in_initiated');

    if (isPlatformBrowser(this.platformId)) {
      // Mirror the ACI teaser's "Sign Into Free AMA Account" flow: if the user took the ACI
      // survey before signing in (results still pending in localStorage), send them to the
      // search results after login instead of back to /residency-alignment/aci, which would
      // re-enter the survey flow. authService.login() uses the current URL as the SAML
      // destination, so rewrite it before initiating login.
      if ('aciSurveyResults' in localStorage) {
        this.location.replaceState('/search/list', 'spec=42931&fromHeader=true');
      }
      this.authService.login();
    }
  }

  public signOut() {
    this.userIdle.stopTimer();
    this.userIdle.stopWatching();
    this.showIdleModal.set(false);
    this.showUserLoggedOutModal.set(false);
    sessionStorage.removeItem('autoLoginInProgress');
    sessionStorage.removeItem('staleJwtLoginAttempted');
    nrAction('user_signed_out');
    this.authService.logout();
  }

  public ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();

    if (this.getCurrentUserSubscription) {
      this.getCurrentUserSubscription.unsubscribe();
    }

    if (this.getMenuItemsSubscription) {
      this.getMenuItemsSubscription.unsubscribe();
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
